import { For, createSignal, onCleanup, onMount } from 'solid-js';
import {
  nextCheckmatePuzzle,
  pieceEndsOn,
  type CheckmatePuzzle,
} from '@domain/checkmate/catalog';
import {
  CHECKMATE_CATEGORIES,
  isCheckmateCategory,
  type CheckmateFilter,
} from '@domain/checkmate/kinds';
import { recordModeAttempt } from '@state/modeStore';
import {
  loadTacticSolution,
  newGame,
  setOrientation,
  setRevealCorrectSquares,
} from '@state/actions';
import { gameStore } from '@state/gameStore';
import { GameControls } from '@ui/Controls/GameControls';
import './CheckmateQuiz.css';

export function CheckmateQuiz() {
  const [filter, setFilter] = createSignal<CheckmateFilter>('all');
  const [puzzle, setPuzzle] = createSignal<CheckmatePuzzle | null>(
    nextCheckmatePuzzle('all'),
  );
  const [revealed, setRevealed] = createSignal(false);

  function present(
    next: CheckmatePuzzle | null,
    nextFilter: CheckmateFilter = filter(),
  ) {
    setFilter(nextFilter);
    setPuzzle(next);
    setRevealed(false);
    setOrientation('white');
    if (next) newGame(next.fen);
    else newGame();
  }

  onMount(() => {
    present(puzzle());
  });

  onCleanup(() => {
    newGame();
  });

  function onReveal() {
    const p = puzzle();
    if (!p) return;
    const won =
      pieceEndsOn(p.from, gameStore.history.slice(0, gameStore.cursor)) === p.to;
    recordModeAttempt('mating', won);
    const loaded = loadTacticSolution({
      startFen: p.fen,
      from: p.from,
      to: p.to,
      name: p.name,
      promotion: p.promotion,
    });
    if (loaded && won) setRevealCorrectSquares([p.to]);
    setRevealed(true);
  }

  function onNext() {
    present(nextCheckmatePuzzle(filter(), { excludeFen: puzzle()?.fen }));
  }

  function resetQuiz() {
    const p = puzzle();
    setRevealed(false);
    setOrientation('white');
    if (p) newGame(p.fen);
    else newGame();
  }

  function onFilterChange(value: string) {
    const next: CheckmateFilter = isCheckmateCategory(value) ? value : 'all';
    present(nextCheckmatePuzzle(next), next);
  }

  const hasPuzzle = () => puzzle() !== null;

  return (
    <>
      <section class="game-rules" aria-label="Game rules">
        <p>Play the one move that checkmates, then reveal the solution.</p>
      </section>
      <GameControls onReset={resetQuiz} />
      <h2 class="checkmate__name">{puzzle()?.name ?? 'Checkmate'}</h2>
      <div class="checkmate__toolbar">
        <select
          class="checkmate__select"
          aria-label="Checkmate category"
          value={filter()}
          onChange={(e) => onFilterChange(e.currentTarget.value)}
        >
          <option value="all">All</option>
          <For each={CHECKMATE_CATEGORIES}>
            {(c) => (
              <option value={c.id}>{c.label}</option>
            )}
          </For>
        </select>
        <button
          type="button"
          class="btn checkmate__reveal"
          disabled={!hasPuzzle()}
          onClick={() => (revealed() ? onNext() : onReveal())}
        >
          {revealed() ? 'Next' : 'Reveal'}
        </button>
      </div>
    </>
  );
}
